import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { formatMenuInstant } from '../lib/date'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import { RoleLabels } from '../auth/rbac'
import { signupRequestStatusLabel } from '../lib/signupRequestStatus'
import MenuGuideButton from '../components/MenuGuideButton'

export default function SignupRequestsPage() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [total, setTotal] = useState(null)

  const navigate = useNavigate()

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get('/signups', { params: { page: 0, size: 50 } })
      const payload = res?.data?.data ?? res?.data
      const content = payload?.content ?? payload?.items ?? []
      setItems(content)
      if (typeof payload?.totalElements === 'number') setTotal(payload.totalElements)
    } catch (e) {
      setError('가입 신청 목록을 불러오지 못했습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleDecision = async (id, action) => {
    const ok = window.confirm(action === 'approve' ? '해당 가입 신청을 승인하시겠습니까?' : '해당 가입 신청을 거절하시겠습니까?')
    if (!ok) return
    setLoading(true)
    setError(null)
    setNotice(null)
    try {
      await api.post(`/signups/${id}/${action}`, {})
      await load()
    } catch (e) {
      setError('처리에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const handleApproveAll = async () => {
    const cntText = typeof total === 'number' ? `${total}건` : '목록에 보이는 대기 신청'
    const ok = window.confirm(`${cntText}을(를) 전체 승인하시겠습니까?\n(실패한 건이 일부 있어도 나머지는 계속 처리됩니다.)`)
    if (!ok) return

    setLoading(true)
    setError(null)
    setNotice(null)
    try {
      const res = await api.post('/signups/approve-all', {})
      const payload = res?.data?.data ?? res?.data
      const approved = payload?.approved ?? 0
      const failed = payload?.failed ?? 0
      const attempted = payload?.attempted ?? 0
      setNotice(`전체 승인 처리 완료: 승인 ${approved} / 시도 ${attempted}${failed ? ` (실패 ${failed})` : ''}`)
      await load()
    } catch (e) {
      setError('전체 승인 처리에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">가입 신청 승인</h1>
          <MenuGuideButton slug="signups" title="가입신청 승인 가이드" />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleApproveAll}
            disabled={loading || (items?.length ?? 0) === 0}
            className="rounded bg-emerald-600 text-white px-3 py-1 text-sm disabled:opacity-50"
            title="현재 승인 가능한 대기 신청을 전체 승인합니다."
          >
            전체 승인
          </button>
          <button
            type="button"
            onClick={load}
            className="rounded border px-3 py-1 text-sm"
          >
            새로고침
          </button>
        </div>
      </div>

      {error && <InlineError message={error} />}
      {notice && (
        <div className="text-sm rounded border border-emerald-200 bg-emerald-50 text-emerald-800 px-3 py-2">
          {notice}
        </div>
      )}

      {loading ? (
        <div className="py-8 flex justify-center">
          <Loading label="불러오는 중..." />
        </div>
      ) : (
        <div className="overflow-x-auto border rounded-md">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left">ID</th>
                <th className="px-3 py-2 text-left">신청일</th>
                <th className="px-3 py-2 text-left">이메일</th>
                <th className="px-3 py-2 text-left">이름</th>
                <th className="px-3 py-2 text-left">조직</th>
                <th className="px-3 py-2 text-left">역할</th>
                <th className="px-3 py-2 text-left">상태</th>
                <th className="px-3 py-2 text-left">액션</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td className="px-3 py-4 text-center text-slate-500" colSpan={8}>
                    대기 중인 가입 신청이 없습니다.
                  </td>
                </tr>
              )}
              {items.map(item => (
                <tr key={item.id} className="border-t">
                  <td className="px-3 py-2">{item.id}</td>
                  <td className="px-3 py-2">
                    {formatMenuInstant(item, 'createdAt')}
                  </td>
                  <td className="px-3 py-2">{item.email}</td>
                  <td className="px-3 py-2">{item.name}</td>
                  <td className="px-3 py-2">
                    {item.orgName ?? '-'}
                    {item.personalJoin && (
                      <span className="ml-1 text-xs bg-slate-200 text-slate-700 rounded px-1.5 py-0.5">개인</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{RoleLabels[item.requestedRole] || item.requestedRole}</td>
                  <td className="px-3 py-2">{signupRequestStatusLabel(item.status)}</td>
                  <td className="px-3 py-2 space-x-2">
                    <button
                      type="button"
                      onClick={() => navigate(`/signups/${item.id}`)}
                      className="px-2 py-1 rounded border text-xs mr-2"
                    >
                      상세
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDecision(item.id, 'approve')}
                      className="px-2 py-1 rounded bg-emerald-600 text-white text-xs"
                    >
                      승인
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDecision(item.id, 'reject')}
                      className="px-2 py-1 rounded bg-red-600 text-white text-xs"
                    >
                      거절
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}


